
<script>
import { reactive } from 'vue'
const state = reactive({
  visible: false,
  title: 'Unsaved changes',
  message: 'You have unsaved changes. If you leave, they will be discarded.',
  confirmText: 'Discard',
  cancelText: 'Keep editing',
  resolve: null,
})
export function confirmDiscard(isDirty = true, opts = {}) {
  if (!isDirty) return Promise.resolve(true)

  return new Promise((resolve) => {
    state.title = opts.title || 'Unsaved changes'
    state.message = opts.message || 'You have unsaved changes. If you leave, they will be discarded.'
    state.confirmText = opts.confirmText || 'Discard'
    state.cancelText = opts.cancelText || 'Keep editing'
    state.resolve = resolve
    state.visible = true
  })
}
</script>
<script setup>
function choose(result) {
  state.visible = false
  if (state.resolve) {
    state.resolve(result)
    state.resolve = null
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="warn-fade">
      <div v-if="state.visible" class="warn-backdrop" @click.self="choose(false)">
        <div class="warn-card">
          <div class="warn-badge">
            <span class="warn-badge-ring"></span>
            <span class="warn-badge-mark">!</span>
          </div>

          <h3 class="warn-title">{{ state.title }}</h3>
          <p class="warn-message">{{ state.message }}</p>

          <div class="warn-actions">
            <button class="warn-btn warn-btn-keep" @click="choose(false)">
              {{ state.cancelText }}
            </button>
            <button class="warn-btn warn-btn-discard" @click="choose(true)">
              {{ state.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
<style scoped>
.warn-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 16px;
}

.warn-card {
  position: relative;
  width: 100%;
  max-width: 360px;
  background: #ffffff;
  border-radius: 16px;
  padding: 28px 24px 22px;
  text-align: center;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.warn-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--pp-accent), var(--pp-accent-text));
}

.warn-badge {
  position: relative;
  width: 48px;
  height: 48px;
  margin: 4px auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.warn-badge-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--pp-accent-light);
  animation: warn-pulse 1.8s ease-out infinite;
}

.warn-badge-mark {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--pp-accent);
  color: #fff;
  font-weight: 800;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes warn-pulse {
  0% { box-shadow: 0 0 0 0 rgba(70, 66, 201, 0.35); }
  70% { box-shadow: 0 0 0 10px rgba(70, 66, 201, 0); }
  100% { box-shadow: 0 0 0 0 rgba(70, 66, 201, 0); }
}

.warn-title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #111827;
}

.warn-message {
  margin: 0 0 22px;
  font-size: 13.5px;
  line-height: 1.5;
  color: #6b7280;
}

.warn-actions {
  display: flex;
  gap: 10px;
}

.warn-btn {
  flex: 1;
  padding: 11px 14px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.05s ease, background 0.15s ease;
}

.warn-btn:active {
  transform: scale(0.97);
}

.warn-btn-keep {
  background: var(--pp-accent-light);
  color: var(--pp-accent);
}

.warn-btn-keep:hover {
  background: var(--pp-accent-border);
}

.warn-btn-discard {
  background: linear-gradient(90deg, var(--pp-accent), var(--pp-accent-text));
  color: #fff;
}

.warn-btn-discard:hover {
  filter: brightness(1.05);
}

.warn-fade-enter-active,
.warn-fade-leave-active {
  transition: opacity 0.15s ease;
}
.warn-fade-enter-from,
.warn-fade-leave-to {
  opacity: 0;
}

.warn-fade-enter-active .warn-card {
  animation: warn-pop 0.18s ease;
}

@keyframes warn-pop {
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>